// NPM dependencies
import React, {
  useState, useCallback, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  Heading, Button, Link, ProgressCircular,
} from '@americanexpress/dls-react';
import { IconSuccess, IconWarning } from '@americanexpress/dls-icons';
import { useSelector } from 'react-redux';
import { useFetchye } from 'fetchye';

// Local dependencies
import useSpcBbkCompatibility from '../../hooks/useSpcBbkCompatibility';
import { useCreatePasskey } from '../../hooks/usePasskey';
import Header from './Header/Header';
import Footer from './Footer/Footer';
import RegistrationSuccess from './RegistrationSuccess/RegistrationSuccess';
import RegistrationError from './RegistrationError/RegistrationError';
import RegistrationFallbackVerified from './RegistrationFallbackVerified/RegistrationFallbackVerified';
import buildFetchOptions from '../../utils/fetchOptions';
import ENDPOINTS from '../../utils/endpoints';
import SHARED from '../../utils/sharedConstants';
import devLog from '../../utils/devLog';
import styles from './PasskeyRegistration.scss';

const toBase64Url = (buffer) => btoa(String.fromCharCode(...new Uint8Array(buffer)))
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const FaceIdIcon = React.memo(() => (
  <svg width="48" height="48" viewBox="0 0 41 41" fill="none" aria-hidden="true">
    <path d="M0.900082 26.3575C1.39692 26.3578 1.79915 26.7598 1.7992 27.2567L1.80113 31.7465C1.80115 35.8563 5.14453 39.2011 9.25592 39.2011L13.7428 39.2011C14.2399 39.2011 14.6429 39.6032 14.6429 40.1003C14.6429 40.5973 14.2399 41.0004 13.7428 41.0004L9.25399 41.0004C4.1521 41.0004 2.89733e-05 36.8487 7.63883e-07 31.7465L6.55849e-07 27.2567C4.6427e-05 26.7597 0.403039 26.3575 0.900082 26.3575ZM40.0984 26.3575C40.5953 26.3575 40.9983 26.7598 40.9985 27.2567L41.0004 31.7465C41.0004 36.8483 36.8486 41.0004 31.7464 41.0004L27.2566 41.0004C26.7596 41.0003 26.3575 40.5973 26.3575 40.1003C26.3575 39.6033 26.7596 39.2012 27.2566 39.2011L31.7445 39.2011C35.8542 39.2011 39.1992 35.8578 39.1993 31.7465L39.1993 27.2567C39.1993 26.7598 39.6015 26.3578 40.0984 26.3575ZM26.3661 29.4917C26.7193 29.1964 27.2334 29.2241 27.554 29.5412L27.6157 29.6085C27.9299 29.9879 27.8777 30.5513 27.5011 30.8662C25.2617 32.7403 22.514 33.6785 19.7681 33.6785C17.0222 33.6784 14.2764 32.7421 12.0351 30.8662C11.6568 30.5512 11.6069 29.9879 11.9196 29.6085C12.2324 29.2292 12.7924 29.1767 13.1691 29.4917C16.9918 32.6917 22.5419 32.6917 26.3661 29.4917ZM21.06 13.1783C21.5597 13.1785 21.9639 13.6057 21.9639 14.1329L21.9639 21.2152C21.9639 23.2423 20.3998 24.8928 18.4781 24.893C17.9783 24.893 17.5748 24.4657 17.5713 23.9403C17.5714 23.4132 17.9764 22.9867 18.4762 22.9867C19.4019 22.9866 20.1551 22.1916 20.1551 21.2152L20.1551 14.1329C20.1551 13.6056 20.5602 13.1783 21.06 13.1783ZM11.7145 14.6429C12.524 14.643 13.1787 15.0964 13.1787 15.6529L13.1787 18.0266C13.1784 18.5845 12.5215 19.0355 11.7145 19.0356C10.9052 19.0356 10.2507 18.5829 10.2503 18.0266L10.2503 15.6529C10.2503 15.0964 10.9049 14.6445 11.7145 14.6429ZM29.2859 14.6429C30.0954 14.6429 30.7501 15.0963 30.7501 15.6529L30.7501 18.0266C30.7497 18.5845 30.0928 19.0356 29.2859 19.0356C28.4789 19.0356 27.822 18.5829 27.8217 18.0266L27.8217 15.6529C27.8217 15.0947 28.4786 14.6429 29.2859 14.6429ZM31.7445 -1.53462e-06C36.8463 -1.78126e-06 40.9985 4.15219 40.9985 9.2549L41.0004 13.7427C41.0004 14.2398 40.5974 14.6429 40.1003 14.6429C39.6033 14.6428 39.2012 14.2398 39.2012 13.7427L39.2012 9.2549C39.2012 5.14469 35.8577 1.79931 31.7464 1.79929L27.2566 1.79929C26.7597 1.79919 26.3577 1.39703 26.3575 0.900138C26.3575 0.403075 26.7596 9.65725e-05 27.2566 -1.31766e-06L31.7445 -1.53462e-06ZM13.7428 -6.64369e-07C14.24 -6.88402e-07 14.6429 0.402997 14.6429 0.900139C14.6427 1.39707 14.2399 1.79929 13.7428 1.79929L9.25303 1.79929C5.14273 1.79929 1.79727 5.14306 1.79727 9.2549L1.7992 13.7427C1.7992 14.2398 1.39627 14.6419 0.899119 14.6419C0.402306 14.6415 4.94626e-05 14.2398 3.30725e-07 13.7447L2.22715e-07 9.25589C0.000206598 4.15362 4.15228 0.0019803 9.25495 0.00198006L13.7428 -6.64369e-07Z" fill="#97999B" />
  </svg>
));
FaceIdIcon.displayName = 'FaceIdIcon';

const FingerprintIcon = React.memo(() => (
  <svg width="48" height="48" viewBox="56 0 38 41" fill="none" aria-hidden="true">
    <path d="M87.2814 5.0635C87.1124 5.0635 86.9434 5.0225 86.7955 4.9405C82.7395 2.911 79.2327 2.05 75.0077 2.05C70.8461 2.05 66.8746 3.0135 63.2622 4.9405C62.7552 5.207 62.1214 5.0225 61.8257 4.5305C61.5511 4.0385 61.7412 3.403 62.2482 3.1365C66.1775 1.025 70.487 0 75.0077 0C79.5285 0 83.4578 0.9635 87.7673 3.075C88.2954 3.3825 88.4855 3.9975 88.2109 4.4895C88.0208 4.8585 87.6828 5.0635 87.2814 5.0635ZM57.0514 15.826C56.8402 15.826 56.6289 15.7645 56.4388 15.6415C55.9952 15.3135 55.8473 14.678 56.1853 14.2065C58.2767 11.3365 60.9384 9.0815 64.1072 7.503C70.7827 4.182 79.2327 4.1615 85.8871 7.4825C89.0559 9.061 91.7177 11.275 93.809 14.145C94.147 14.596 94.0203 15.252 93.5555 15.58C93.0697 15.908 92.4148 15.8055 92.0768 15.375C90.1755 12.751 87.7673 10.7215 84.9154 9.307C78.8525 6.2935 71.0996 6.2935 65.0578 9.3275C62.1848 10.7625 59.7766 12.8125 57.8753 15.375C57.7063 15.6825 57.3894 15.826 57.0514 15.826ZM70.2546 40.5695C69.98 40.5695 69.7265 40.467 69.5152 40.262C67.6773 38.4785 66.6845 37.3305 65.2691 34.85C63.8114 32.3285 63.0509 29.2535 63.0509 25.953C63.0509 19.8645 68.4167 14.9035 75.0077 14.9035C81.5988 14.9035 86.9645 19.8645 86.9645 25.953C86.9645 26.2248 86.8532 26.4856 86.6551 26.6778C86.4571 26.87 86.1884 26.978 85.9083 26.978C85.6281 26.978 85.3595 26.87 85.1614 26.6778C84.9633 26.4856 84.852 26.2248 84.852 25.953C84.852 20.992 80.4369 16.9535 75.0077 16.9535C69.5786 16.9535 65.1635 20.992 65.1635 25.953C65.1635 28.905 65.8395 31.6315 67.1281 33.825C68.4801 36.203 69.4096 37.2075 71.0362 38.8065C71.4376 39.2165 71.4376 39.852 71.0362 40.262C70.7827 40.467 70.5292 40.5695 70.2546 40.5695ZM85.4013 36.777C82.8874 36.777 80.6692 36.162 78.8525 34.9525C75.7049 32.882 73.8247 29.52 73.8247 25.953C73.8247 25.6812 73.936 25.4204 74.1341 25.2282C74.3322 25.036 74.6008 24.928 74.881 24.928C75.1611 24.928 75.4298 25.036 75.6279 25.2282C75.8259 25.4204 75.9372 25.6812 75.9372 25.953C75.9372 28.8435 77.4582 31.57 80.0355 33.251C81.5354 34.235 83.2888 34.7065 85.4013 34.7065C85.9083 34.7065 86.7533 34.645 87.5983 34.5015C88.1686 34.399 88.739 34.768 88.8235 35.342C88.9291 35.875 88.5489 36.4285 87.9574 36.531C86.7533 36.7565 85.697 36.777 85.4013 36.777ZM81.1551 41C81.0706 41 80.965 41 80.8805 41C77.5216 40.057 75.3246 38.8475 73.022 36.654C70.0645 33.825 68.4378 30.012 68.4378 25.953C68.4378 22.632 71.3531 19.926 74.9444 19.926C78.5356 19.926 81.4509 22.632 81.4509 25.953C81.4509 28.1465 83.4578 29.93 85.8449 29.93C88.2743 29.93 90.2389 28.1465 90.2389 25.953C90.2389 18.2245 83.3733 11.9515 74.9232 11.9515C68.9237 11.9515 63.3889 15.1905 60.9596 20.213C60.1357 21.8735 59.7132 23.821 59.7132 25.953C59.7132 27.552 59.8611 30.0735 61.1286 33.3535C61.3398 33.8865 61.0652 34.481 60.5159 34.6655C59.9667 34.85 59.3541 34.5835 59.1639 34.071C58.1077 31.3855 57.6218 28.7 57.6218 25.953C57.6218 23.493 58.1077 21.2585 59.0583 19.311C61.8679 13.5915 68.0998 9.881 74.9232 9.881C84.514 9.881 92.3514 17.0765 92.3514 25.9325C92.3514 29.2535 89.4361 31.9595 85.8449 31.9595C82.2536 31.9595 79.3384 29.2535 79.3384 25.9325C79.3384 23.739 77.3737 21.9555 74.9444 21.9555C72.515 21.9555 70.5503 23.739 70.5503 25.9325C70.5503 29.438 71.9446 32.718 74.5007 35.178C76.5076 37.105 78.43 38.171 81.4086 38.95C81.979 39.114 82.2959 39.688 82.148 40.221C82.0424 40.6925 81.5987 41 81.1551 41Z" fill="#97999B" />
  </svg>
));
FingerprintIcon.displayName = 'FingerprintIcon';

const mockRegistrationOptions = {
  challenge: 'mock-challenge',
  rp: {
    name: 'American Express SafeKey',
    id: 'americanexpress.com',
  },
  user: {
    // Opaque user handle encoded as base64url
    id: Uint8Array.from('test-user-id-for-unit-test', (c) => c.charCodeAt(0)),
    name: 'test.user@test.com',
    displayName: 'Test User',
  },
};

const PasskeyRegistration = ({
  isVerified = true,
  onContinue = undefined,
  onMerchantContinue = undefined,
  sessionData = {},
}) => {
  const intl = useIntl();

  const [isVerifying, setIsVerifying] = useState(false);
  const [currentScreen, setCurrentScreen] = useState('initial');
  const {
    spcAvailable, isSpcBbkLoading, bbkAvailable, deviceContext,
  } = useSpcBbkCompatibility();
  const { STANDARD_TYPE, COMMON, FLOW_TYPE } = SHARED;
  const { CEREMONY_SESSIONS, CEREMONY_COMPLETE } = ENDPOINTS;
  const {
    sessionId,
    challenge,
    maskedCardNumber,
    credentialCount,
    token,
  } = sessionData || {};

  const bffApiUrl = useSelector((state) => state.getIn(['config', 'bffApiUrl']));

  const [completionParams, setCompletionParams] = useState(null);

  const completionUrl = completionParams
    ? `${bffApiUrl}${CEREMONY_SESSIONS}/${sessionId}${CEREMONY_COMPLETE}`
    : false;

  const {
    data: completionData,
    error: completionError,
    run: postRegistrationComplete,
  } = useFetchye(
    completionUrl,
    buildFetchOptions({
      body: completionParams?.body || {},
      defer: true,
      headers: { sessionId, Authorization: `Bearer ${token}` },
    })
  );

  useEffect(() => {
    if (completionData?.status >= 400 || completionError) {
      devLog.error('Registration completion failed:', completionError || completionData);
      setCurrentScreen('error');
    }
  }, [completionData, completionError]);

  useEffect(() => {
    if (!completionParams) return;
    const completeRegistration = async () => {
      try {
        await postRegistrationComplete();
        setCurrentScreen('success');
        if (onContinue) onContinue();
      } catch (err) {
        devLog.error('Registration completion failed:', err);
        setCurrentScreen('error');
      } finally {
        setIsVerifying(false);
        setCompletionParams(null);
      }
    };
    completeRegistration();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when completionParams changes
  }, [completionParams]);

  const {
    isCreating, createPasskey, createSpcBbkPasskey,
  } = useCreatePasskey();

  const checkDeviceCompatibility = useCallback(async () => {
    setCurrentScreen('initial');
    setIsVerifying(true);
    try {
      const registrationOptions = {
        ...mockRegistrationOptions,
        challenge,
      };

      // Use the SPC BBK path when the browser confirms full support;
      // fall back to standard platform passkey (WebAuthn) otherwise.
      let registrationResult;
      if (spcAvailable && bbkAvailable) {
        registrationResult = await createSpcBbkPasskey(registrationOptions);
      } else {
        registrationResult = await createPasskey(registrationOptions);
      }

      devLog.info('Passkey registration result:', registrationResult);
      const { id, response: { attestationObject } } = registrationResult;

      const attestationObjectBase64url = toBase64Url(attestationObject);
      const { DISPLAY_NAME } = COMMON;
      const requestBody = {
        attestationObject: attestationObjectBase64url,
        publicKeyCredential: registrationResult,
        authenticatorData: id,
        displayName: `${DISPLAY_NAME} ${maskedCardNumber} ${credentialCount + 1}`,
        credentialNickname: deviceContext.deviceType,
        standardType: bbkAvailable ? STANDARD_TYPE.SPC_BBK : STANDARD_TYPE.WEBAUTHN,
        ceremonyType: FLOW_TYPE.REGISTRATION,
      };
      setCompletionParams({ body: requestBody });
    } catch (err) {
      // NotAllowedError  → user dismissed the prompt
      // InvalidStateError → credential already registered on this device
      // Any other error  → generic registration failure
      devLog.error('Passkey creation failed:', err?.name, err?.message);
      setCurrentScreen('error');
      setIsVerifying(false);
    }
  }, [
    spcAvailable,
    bbkAvailable,
    deviceContext,
    challenge,
    createSpcBbkPasskey,
    createPasskey,
    STANDARD_TYPE,
    COMMON,
    credentialCount,
    maskedCardNumber,
  ]);

  const showFallbackVerifiedScreen = useCallback(() => {
    setCurrentScreen('fallbackVerified');
  }, []);

  if (!isVerified) {
    return null;
  }

  // Show success screen
  if (currentScreen === 'success') {
    return <RegistrationSuccess onContinue={onMerchantContinue} />;
  }

  // Show error screen
  if (currentScreen === 'error') {
    return (
      <RegistrationError
        onContinue={showFallbackVerifiedScreen}
        onRetry={checkDeviceCompatibility}
      />
    );
  }

  // Show fallback verified screen
  if (currentScreen === 'fallbackVerified') {
    return <RegistrationFallbackVerified />;
  }

  if (isVerifying && !spcAvailable && bbkAvailable === false) {
    return (
      <main className={styles['passkey-registration']} aria-label="Passkey registration">
        <Header />
        <div className={styles['passkey-registration__content']}>
          <div className={styles['passkey-registration__unsupported-badge']}>
            <IconWarning
              className={styles['passkey-registration__unsupported-badge-icon']}
              size="xl"
              isFilled={true}
              aria-hidden={true}
            />
            <span>{intl.formatMessage({ id: 'passkeyRegistration.allSet' })}</span>
          </div>

          <p className={styles['passkey-registration__unsupported-message']}>
            {intl.formatMessage({ id: 'passkeyRegistration.notSupported' })}
          </p>

          <div className={styles['passkey-registration__actions']}>
            <Button variant="primary" onClick={onContinue}>
              {intl.formatMessage({ id: 'passkeyRegistration.continue' })}
            </Button>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className={styles['passkey-registration']} aria-label="Passkey registration">
      <Header />
      {(isVerifying || isCreating) && (
        <div className={styles['passkey-registration__loading-overlay']} aria-live="assertive" aria-label={intl.formatMessage({ id: 'passkeyRegistration.verifying' })}>
          <ProgressCircular size="md" />
        </div>
      )}
      <div className={styles['passkey-registration__content']}>
        <div className={styles['passkey-registration__verified-badge']}>
          {!isVerifying && (
            <IconSuccess
              className={styles['passkey-registration__verified-badge-icon']}
              size="md"
              isFilled={true}
              color="brand"
              title="Verified"
              aria-hidden={true}
            />
          )}
          <div aria-live="polite" aria-atomic="true">
            <span className={styles['passkey-registration__verified-badge-label']}>
              {isVerifying
                ? intl.formatMessage({ id: 'passkeyRegistration.verifying' })
                : intl.formatMessage({ id: 'passkeyRegistration.verified' })}
            </span>
          </div>
        </div>

        <div className={styles['passkey-registration__biometric-icons']}>
          <FaceIdIcon />
          <FingerprintIcon />
        </div>

        <div className={styles['passkey-registration__heading']}>
          <Heading level={1} variant="sans-xlarge-book">
            {intl.formatMessage({ id: 'passkeyRegistration.heading' })}
          </Heading>
        </div>

        <p className={styles['passkey-registration__description']}>
          {intl.formatMessage({ id: 'passkeyRegistration.description' })}
        </p>

        <div className={styles['passkey-registration__how-it-works']}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid
            -- placeholder href until real URL is provided */}
          <Link href="#">
            {intl.formatMessage({ id: 'passkeyRegistration.howItWorks' })}
          </Link>
        </div>

        <p className={styles['passkey-registration__legal-text']}>
          <FormattedMessage
            id="passkeyRegistration.legalText1"
            values={{
              terms: (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid -- placeholder href until real URL is provided
                <Link href="#">
                  {intl.formatMessage({ id: 'passkeyRegistration.terms' })}
                </Link>
              ),
            }}
          />
          {' '}
          <FormattedMessage
            id="passkeyRegistration.legalText2"
            values={{
              privacyNotice: (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid -- placeholder href until real URL is provided
                <Link href="#">
                  {intl.formatMessage({ id: 'passkeyRegistration.privacyNotice' })}
                </Link>
              ),
            }}
          />
        </p>

        <div className={styles['passkey-registration__actions']}>
          <Button
            variant="primary"
            onClick={checkDeviceCompatibility}
            disabled={isSpcBbkLoading || isCreating || isVerifying}
            aria-busy={isCreating || isVerifying}
          >
            {intl.formatMessage({ id: 'passkeyRegistration.createPasskey' })}
          </Button>

          <Button variant="secondary" onClick={showFallbackVerifiedScreen}>
            {intl.formatMessage({ id: 'passkeyRegistration.skip' })}
          </Button>
        </div>
      </div>
      <Footer />
    </main>
  );
};

PasskeyRegistration.propTypes = {
  isVerified: PropTypes.bool,
  sessionData: PropTypes.shape({
    sessionId: PropTypes.string.isRequired,
    challenge: PropTypes.string.isRequired,
    credentialIds: PropTypes.arrayOf(PropTypes.string),
    credentialCount: PropTypes.number.isRequired,
    maskedCardNumber: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
  }),
  onContinue: PropTypes.func,
  onMerchantContinue: PropTypes.func,
};

export default PasskeyRegistration;
